import { CalendarCompact } from "@/components/ui/calendar-compact";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState, useMemo, useEffect } from "react";
import { formatTimezone } from "@/lib/utils";

interface DateTimeSelectorProps {
  selectedDate: Date | undefined;
  setSelectedDate: (date: Date | undefined) => void;
  selectedTime: string;
  setSelectedTime: (time: string) => void;
  initialScheduledFor?: string;
  timezone: string;
}

export const DateTimeSelector = ({
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  initialScheduledFor,
  timezone
}: DateTimeSelectorProps) => {
  useEffect(() => {
    if (initialScheduledFor) {
      const date = new Date(initialScheduledFor);
      setSelectedDate(date);
      setSelectedTime(`${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`);
    }
  }, [initialScheduledFor, setSelectedDate, setSelectedTime]);

  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();

  const hourOptions = Array.from({ length: 24 }, (_, i) => {
    return i.toString().padStart(2, '0');
  });

  const minuteOptions = ['00', '15', '30', '45'];
  const [currentSelectedHour, currentSelectedMinute] = selectedTime.split(':');

  const isCurrentDate = selectedDate?.toDateString() === now.toDateString();

  const availableHours = useMemo(() => {
    if (isCurrentDate) {
      return hourOptions.filter(hour => parseInt(hour) > currentHour || 
        (parseInt(hour) === currentHour && parseInt(minuteOptions[0]) > currentMinute));
    }
    return hourOptions;
  }, [isCurrentDate, currentHour, currentMinute]);

  const availableMinutes = useMemo(() => {
    if (isCurrentDate && parseInt(currentSelectedHour) === currentHour) {
      return minuteOptions.filter(minute => parseInt(minute) > currentMinute);
    }
    return minuteOptions;
  }, [isCurrentDate, currentSelectedHour, currentHour, currentMinute]);

  const handleTimeChange = (type: 'hour' | 'minute', value: string) => {
    const [hour, minute] = selectedTime.split(':');
    if (type === 'hour') {
      setSelectedTime(`${value}:${minute}`);
    } else {
      setSelectedTime(`${hour}:${value}`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-center">
        <div className="w-[300px]">
          <CalendarCompact
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            disabled={(date) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const compareDate = new Date(date);
              compareDate.setHours(0, 0, 0, 0);
              return compareDate < today;
            }}
            className="rounded-md border"
          />
        </div>
      </div>
      <div className="space-y-2">
        <div className="space-y-1">
          <label htmlFor="time" className="text-sm font-medium">
            Time
          </label>
          <p className="text-sm text-muted-foreground">
            Timezone: {formatTimezone(timezone)}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={currentSelectedHour} onValueChange={(value) => handleTimeChange('hour', value)}>
            <SelectTrigger className="w-[110px]">
              <SelectValue placeholder="Hour" />
            </SelectTrigger>
            <SelectContent className="max-h-[200px]">
              {availableHours.map((hour) => (
                <SelectItem key={hour} value={hour}>
                  {hour}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm">:</span>
          <Select value={currentSelectedMinute} onValueChange={(value) => handleTimeChange('minute', value)}>
            <SelectTrigger className="w-[110px]">
              <SelectValue placeholder="Minute" />
            </SelectTrigger>
            <SelectContent className="max-h-[200px]">
              {availableMinutes.map((minute) => (
                <SelectItem key={minute} value={minute}>
                  {minute}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};